import { useState, type JSX } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useAppVersion } from '@/hooks/use-app-version';

// Aviso de versión nueva ------------------------------------------------------
//
// Tarjeta fija abajo a la derecha: al ser `fixed` no empuja nada del layout, así
// que las tablas anchas siguen midiendo igual. Abajo a la izquierda ya vive el
// botón de React Query Devtools, por eso va del otro lado.
//
// Nunca recarga sola — quien está a mitad de un formulario decide cuándo.

/** Clave de la marca de descarte. `sessionStorage`: vive solo en esta pestaña. */
export const APP_UPDATE_DISMISS_STORAGE_KEY = 'commerce-engine:app-update-dismissed';

// La marca guarda el build que se descartó, no un booleano: después de recargar,
// la pestaña corre otro build y el aviso vuelve a estar disponible.
function readDismissedBuildId(): string | null {
  try {
    return window.sessionStorage.getItem(APP_UPDATE_DISMISS_STORAGE_KEY);
  } catch {
    // Modo privado o storage bloqueado por política: sin marca previa.
    return null;
  }
}

function markDismissed(buildId: string): void {
  try {
    window.sessionStorage.setItem(APP_UPDATE_DISMISS_STORAGE_KEY, buildId);
  } catch {
    // Sin storage el descarte dura lo que dure este montaje. Alcanza.
  }
}

export const AppUpdateBanner = (): JSX.Element | null => {
  const { isOutdated, buildId } = useAppVersion();
  const [dismissedBuildId, setDismissedBuildId] = useState<string | null>(readDismissedBuildId);

  if (!isOutdated || dismissedBuildId === buildId) return null;

  const handleReload = (): void => {
    window.location.reload();
  };

  const handleDismiss = (): void => {
    markDismissed(buildId);
    setDismissedBuildId(buildId);
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="animate-slide-up fixed right-4 bottom-4 z-70 w-[min(22rem,calc(100vw-2rem))]"
    >
      <Card className="flex items-start gap-3 p-4 shadow-lg">
        <div
          className="bg-surface-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
          aria-hidden="true"
        >
          <RefreshCw className="text-info h-4 w-4" />
        </div>
        <div className="flex-1 space-y-2">
          <p className="text-sm font-medium">Hay una versión nueva disponible</p>
          <p className="text-text-muted text-xs">
            Actualiza cuando termines lo que estás haciendo. La página se recarga.
          </p>
          <Button type="button" size="sm" onClick={handleReload}>
            Actualizar
          </Button>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Descartar aviso"
          onClick={handleDismiss}
          className="h-8 w-8 shrink-0"
        >
          <X className="h-4 w-4" />
        </Button>
      </Card>
    </div>
  );
};
